// All Calculations Use An Object with the params as keys and will just be passed in the state of the particular calculator.

// Real Values
const MONTHS_PER_YEAR = 12;

// Fuzzy Factors. Should eventually be more scientific
const MAINTANENCE_FACTOR = 0.03;
const OPPORTUNITY_COST_INTEREST = 0.07;

const ESTIMATED_VALUE_YEARS = 30;
const ESTIMATED_VALUE_APPRECIATION = 0.03;

const calculateValueAtMaturityMonthly = ({ initialInvestment, monthlyAddition, interestRate, yearsUntilMaturity }) => {
    let value = initialInvestment;
    const monthsUntilMaturity = yearsUntilMaturity * MONTHS_PER_YEAR;
    const monthlyRate = (interestRate / 100) / MONTHS_PER_YEAR;

    for (let months = 0; months < monthsUntilMaturity; months++) {
        value += (value * monthlyRate) + monthlyAddition;
    }

    return value;
};

const calculateValueAtMaturityYearly = ({ initialInvestment, monthlyAddition, interestRate, yearsUntilMaturity }) => {
    let value = initialInvestment;
    const yearlyAddition = monthlyAddition * MONTHS_PER_YEAR;

    for (let years = 0; years < yearsUntilMaturity; years++) {
        value += (value * interestRate / 100) + yearlyAddition;
    }

    return value;
};

export const calculateAssetOutputs = ({ interestRate, loanPeriod, costOfAsset, downPayment, salesTax, propertyTax, feesYearly, ownershipPeriod, usageCost, insurance, maintenenceYearly, depreciationRate }) => {
    const interestRateMonthly = interestRate / (100 * MONTHS_PER_YEAR);
    const loanPeriodMonthly = loanPeriod * MONTHS_PER_YEAR;
    const ownershipPeriodMonthly = (ownershipPeriod || loanPeriod) * MONTHS_PER_YEAR;
    const loanPeriodDivisor = loanPeriodMonthly || ownershipPeriodMonthly;
    const monthlyPaymentBasic = interestRate ? (interestRateMonthly / (1 - ((1 + interestRateMonthly) ** (-1 * loanPeriodMonthly)))) * (costOfAsset - downPayment) : (costOfAsset - downPayment) / loanPeriodDivisor;
    const monthlyPaymentAdvanced = interestRate ? (interestRateMonthly / (1 - ((1 + interestRateMonthly) ** (-1 * loanPeriodMonthly)))) * ((costOfAsset * (1 + salesTax / 100)) - downPayment) : ((costOfAsset * (1 + salesTax / 100)) - downPayment) / loanPeriodDivisor;
    const monthlyCostOfOperation = insurance + usageCost + ((feesYearly + maintenenceYearly) / MONTHS_PER_YEAR);
    const paymentAndOperationsCost = (monthlyPaymentAdvanced * loanPeriodDivisor) + (monthlyCostOfOperation * ownershipPeriodMonthly);

    const ocInterestRateMonthly = OPPORTUNITY_COST_INTEREST / MONTHS_PER_YEAR;
    const ocLoanAmount1 = monthlyPaymentAdvanced * (((1 + ocInterestRateMonthly) ** (loanPeriodMonthly) - 1) / ocInterestRateMonthly);
    const ocLoanAmount2 = downPayment * ((1 + OPPORTUNITY_COST_INTEREST) ** (loanPeriod));
    const ocMonthlyAdditions = monthlyCostOfOperation * (((1 + ocInterestRateMonthly) ** (ownershipPeriodMonthly) - 1) / ocInterestRateMonthly);
    const ocPotentialEarnings = ocLoanAmount1 + ocLoanAmount2 + ocMonthlyAdditions;
    const opportunityCost = ocPotentialEarnings - paymentAndOperationsCost;

    const valueEOL = ((1 - (depreciationRate || 0) / 100) ** ownershipPeriod) * costOfAsset;
    const depreciationCost = costOfAsset - valueEOL;

    const totalCost = paymentAndOperationsCost + opportunityCost + depreciationCost - valueEOL;

    return { monthlyPaymentBasic, monthlyPaymentAdvanced, monthlyCostOfOperation, paymentAndOperationsCost, opportunityCost, depreciationCost, valueEOL, totalCost };
};

// refactor this based on ^
export const calculateValueAtMaturity = (state) => {
    const maturityCalculator = state.compoundingFrequency === 'monthly' ? calculateValueAtMaturityMonthly : calculateValueAtMaturityYearly;
    return maturityCalculator(state);
};

export const calculateHomeOutputs = ({
    interestRate,
    loanPeriod,
    costOfAsset,
    downPayment,
    propertyTax,
    insurance,
    pmi,
    hoa,
    usageCost,
    maintenenceYearly,
}) => {
    const interestRateMonthly = interestRate / (100 * MONTHS_PER_YEAR);
    const loanPeriodMonthly = loanPeriod * MONTHS_PER_YEAR;
    const loanAmount = costOfAsset - downPayment;
    const monthlyPrincipal = loanAmount / loanPeriodMonthly;
    const monthlyPrincipalWithInterest = (interestRateMonthly / (1 - ((1 + interestRateMonthly) ** (-1 * loanPeriodMonthly)))) * loanAmount;
    const monthlyTax = costOfAsset * (propertyTax / 100) / 12;
    const totalPrincipalAndInterest = monthlyPrincipalWithInterest * loanPeriodMonthly;
    const totalInterest = totalPrincipalAndInterest - loanAmount;
    const utilitiesMonthly = usageCost;
    const maintenenceMonthly = maintenenceYearly / MONTHS_PER_YEAR;
    const operationalCostsMonthly = utilitiesMonthly + maintenenceMonthly;
    const totalOperationalCosts = operationalCostsMonthly * loanPeriodMonthly;
    const totalPrincipalInterestAndOperationalCosts = totalPrincipalAndInterest + totalOperationalCosts;
    const monthlyTaxesAndFees = monthlyTax + insurance + pmi + hoa;

    const ocInterestRateMonthly = OPPORTUNITY_COST_INTEREST / MONTHS_PER_YEAR;
    const ocLoanAmount1 = monthlyPrincipalWithInterest * (((1 + ocInterestRateMonthly) ** (loanPeriodMonthly) - 1) / ocInterestRateMonthly);
    const ocLoanAmount2 = downPayment * ((1 + OPPORTUNITY_COST_INTEREST) ** (loanPeriod));
    const ocMonthlyAdditions = (operationalCostsMonthly + monthlyTaxesAndFees) * (((1 + ocInterestRateMonthly) ** (loanPeriodMonthly) - 1) / ocInterestRateMonthly);
    const ocPotentialEarnings = ocLoanAmount1 + ocLoanAmount2 + ocMonthlyAdditions;
    const opportunityCost = ocPotentialEarnings - totalPrincipalInterestAndOperationalCosts;
    const estimatedValue = ((1 + (ESTIMATED_VALUE_APPRECIATION || 0)) ** ESTIMATED_VALUE_YEARS) * costOfAsset;

    return {
        monthlyPrincipal,
        monthlyInterest: monthlyPrincipalWithInterest - monthlyPrincipal,
        monthlyTax,
        insurance,
        pmi,
        hoa,
        totalInterest,
        totalPrincipalAndInterest,
        utilitiesMonthly,
        maintenenceMonthly,
        operationalCostsMonthly,
        totalOperationalCosts,
        totalPrincipalInterestAndOperationalCosts,
        opportunityCost,
        estimatedValue,
    };
};
